import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useFavorites } from '../contexts/FavoritesContext';
import { formatMoney } from '../utils/formatters';
import MarketTicker from '../components/MarketTicker';
import LoginPromptModal from '../components/LoginPromptModal';
import LocationSheet from '../components/LocationSheet';
import { SUDAN_CROPS, getUnitLabel } from '../config/sudanCrops';
import {
  Search, Heart, MapPin, X, ChevronRight, RefreshCw, Plus,
  Navigation, Loader2, Package, TrendingUp
} from 'lucide-react';
import { useUnifiedSearch } from '../hooks/useUnifiedSearch';
import { toAbsoluteUrl } from '../lib/media';
import './CropHome.css';

// FULL OFFICIAL LIST - ALL 18 SUDAN STATES
export const SUDAN_STATES = [
  { key: 'all', name_en: 'All Sudan', name_ar: 'كل السودان' },
  { key: 'khartoum', name_en: 'Khartoum', name_ar: 'الخرطوم' },
  { key: 'gezira', name_en: 'Gezira', name_ar: 'الجزيرة' },
  { key: 'gedaref', name_en: 'Gedaref', name_ar: 'القضارف' },
  { key: 'kassala', name_en: 'Kassala', name_ar: 'كسلا' },
  { key: 'sennar', name_en: 'Sennar', name_ar: 'سنار' },
  { key: 'blue_nile', name_en: 'Blue Nile', name_ar: 'النيل الأزرق' },
  { key: 'white_nile', name_en: 'White Nile', name_ar: 'النيل الأبيض' },
  { key: 'north_kordofan', name_en: 'North Kordofan', name_ar: 'شمال كردفان' },
  { key: 'south_kordofan', name_en: 'South Kordofan', name_ar: 'جنوب كردفان' },
  { key: 'north_darfur', name_en: 'North Darfur', name_ar: 'شمال دارفور' },
  { key: 'south_darfur', name_en: 'South Darfur', name_ar: 'جنوب دارفور' },
  { key: 'east_darfur', name_en: 'East Darfur', name_ar: 'شرق دارفور' },
  { key: 'west_darfur', name_en: 'West Darfur', name_ar: 'غرب دارفور' },
  { key: 'central_darfur', name_en: 'Central Darfur', name_ar: 'وسط دارفور' },
  { key: 'river_nile', name_en: 'River Nile', name_ar: 'نهر النيل' },
  { key: 'northern', name_en: 'Northern', name_ar: 'الشمالية' },
  { key: 'red_sea', name_en: 'Red Sea', name_ar: 'البحر الأحمر' },
];

// Enhanced crop categories with market data hints
const CROP_CATEGORIES = [
  { slug: 'sesame', name_en: 'Sesame', name_ar: 'سمسم', icon: '🌾', color: '#F59E0B', unit: 'jawal', priceHint: '50,000' },
  { slug: 'sorghum', name_en: 'Sorghum', name_ar: 'ذرة', icon: '🌽', color: '#EF4444', unit: 'quintar', priceHint: '18,000' },
  { slug: 'groundnuts', name_en: 'Groundnuts', name_ar: 'فول سوداني', icon: '🥜', color: '#D97706', unit: 'jawal', priceHint: '35,000' },
  { slug: 'onions', name_en: 'Onions', name_ar: 'بصل', icon: '🧅', color: '#8B5CF6', unit: 'crate', priceHint: '20,000' },
  { slug: 'cotton', name_en: 'Cotton', name_ar: 'قطن', icon: '☁️', color: '#3B82F6', unit: 'quintar', priceHint: '75,000' },
  { slug: 'wheat', name_en: 'Wheat', name_ar: 'قمح', icon: '🌾', color: '#F97316', unit: 'quintar', priceHint: '22,000' },
  { slug: 'millet', name_en: 'Millet', name_ar: 'دخن', icon: '🌾', color: '#84CC16', unit: 'quintar', priceHint: '15,000' },
  { slug: 'livestock', name_en: 'Livestock', name_ar: 'مواشي', icon: '🐄', color: '#10B981', unit: 'head', priceHint: '' },
  { slug: 'farm_services', name_en: 'Farm Services', name_ar: 'خدمات زراعية', icon: '🚜', color: '#6366F1', unit: 'service', priceHint: '' },
];

// Sample ads to seed the experience (shown when no real ads exist)
const SAMPLE_ADS = {
  sesame: [
    { title_ar: 'سمسم أبيض ممتاز - 50 جوال', title_en: 'Premium White Sesame - 50 bags', price: 50000, state: 'gedaref', city: 'القضارف' },
    { title_ar: 'سمسم للتصدير - جودة عالية', title_en: 'Export Quality Sesame', price: 55000, state: 'kassala', city: 'كسلا' },
    { title_ar: 'سمسم محصول جديد 2024', title_en: 'New Harvest Sesame 2024', price: 48000, state: 'sennar', city: 'سنار' },
  ],
  sorghum: [
    { title_ar: 'ذرة فتريتة - 100 قنطار', title_en: 'Feterita Sorghum - 100 quintars', price: 18000, state: 'gezira', city: 'ود مدني' },
    { title_ar: 'ذرة علف للمواشي', title_en: 'Fodder Sorghum for Livestock', price: 15000, state: 'white_nile', city: 'ربك' },
  ],
  groundnuts: [
    { title_ar: 'فول سوداني مقشور - 30 جوال', title_en: 'Shelled Groundnuts - 30 bags', price: 38000, state: 'north_kordofan', city: 'الأبيض' },
    { title_ar: 'فول للزيت - جودة ممتازة', title_en: 'Oil-grade Groundnuts', price: 35000, state: 'south_kordofan', city: 'كادوقلي' },
  ],
  onions: [
    { title_ar: 'بصل أحمر - 100 قفة', title_en: 'Red Onions - 100 crates', price: 22000, state: 'gedaref', city: 'القضارف' },
    { title_ar: 'بصل للتصدير - معبأ', title_en: 'Export Onions - Packed', price: 25000, state: 'kassala', city: 'كسلا' },
    { title_ar: 'بصل طازج من المزرعة', title_en: 'Fresh Farm Onions', price: 20000, state: 'river_nile', city: 'عطبرة' },
  ],
  cotton: [
    { title_ar: 'قطن طويل التيلة - 50 قنطار', title_en: 'Long Staple Cotton - 50 quintars', price: 78000, state: 'gezira', city: 'ود مدني' },
    { title_ar: 'قطن محلوج - جاهز للتسليم', title_en: 'Ginned Cotton - Ready', price: 75000, state: 'white_nile', city: 'كوستي' },
  ],
  wheat: [
    { title_ar: 'قمح محلي ممتاز - 80 قنطار', title_en: 'Premium Local Wheat - 80 quintars', price: 24000, state: 'gezira', city: 'الحصاحيصا' },
    { title_ar: 'قمح للطحن - جودة عالية', title_en: 'Milling Wheat - High Grade', price: 22000, state: 'northern', city: 'دنقلا' },
  ],
  millet: [
    { title_ar: 'دخن محلي - 40 قنطار', title_en: 'Local Millet - 40 quintars', price: 16000, state: 'north_kordofan', city: 'الأبيض' },
    { title_ar: 'دخن للعصيدة - نظيف', title_en: 'Clean Millet for Asida', price: 15000, state: 'west_darfur', city: 'الجنينة' },
  ],
  livestock: [
    { title_ar: 'عجول بقري - 3 سنوات', title_en: 'Bull Calf - 3 years', price: 850000, state: 'south_darfur', city: 'نيالا' },
    { title_ar: 'غنم حمري - 10 رؤوس', title_en: 'Hamari Sheep - 10 heads', price: 45000, state: 'north_kordofan', city: 'النهود' },
  ],
  farm_services: [
    { title_ar: 'خدمة حراثة بالجرار', title_en: 'Tractor Plowing Service', price: 5000, state: 'gezira', city: 'ود مدني' },
    { title_ar: 'خدمة حصاد آلي', title_en: 'Mechanical Harvesting', price: 8000, state: 'gedaref', city: 'القضارف' },
  ],
};

function CropHome() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const { cropSlug } = useParams(); // Get crop from URL if navigated to /crop/:cropSlug
  const isArabic = i18n.language?.startsWith('ar');
  const isRTL = document.dir === 'rtl' || isArabic;
  const navigate = useNavigate();
  
  // State
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedState, setSelectedState] = useState('all');
  const [selectedCrop, setSelectedCrop] = useState(cropSlug || null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const { isFavorite, toggleFavorite } = useFavorites();
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [loginRedirectAction, setLoginRedirectAction] = useState(null);
  const [showLocationSheet, setShowLocationSheet] = useState(false);
  const [cropCounts, setCropCounts] = useState({});
  const pageSize = 20;
  
  // Get selected state display name
  const selectedStateData = useMemo(() => {
    return SUDAN_STATES.find(s => s.key === selectedState) || SUDAN_STATES[0];
  }, [selectedState]);

  // Get selected crop data
  const selectedCropData = useMemo(() => {
    return CROP_CATEGORIES.find(c => c.slug === selectedCrop);
  }, [selectedCrop]);
  
  // Helper function to get full image URL — iter275: delegate to canonical
  // resolver so production custom domain uses same-origin (not stale preview).
  const getImageUrl = (imagePath) => {
    if (!imagePath) return '/placeholder.svg';
    return toAbsoluteUrl(imagePath);
  };

  const handleImageError = (e) => {
    e.target.onerror = null;
    e.target.src = '/placeholder.svg';
    e.target.parentElement?.classList.add('loaded');
  };

  const handleImageLoad = (e) => {
    e.target.parentElement?.classList.add('loaded');
  };

  // Update selected crop when URL changes
  useEffect(() => {
    if (cropSlug) {
      setSelectedCrop(cropSlug);
    }
  }, [cropSlug]);

  // Fetch products when filters change
  useEffect(() => {
    setPage(1);
    fetchProducts(1, true);
  }, [selectedState, selectedCrop, searchQuery]);

  // Fetch crop counts on mount
  useEffect(() => {
    fetchCropCounts();
  }, [selectedState]);

  const fetchCropCounts = async () => {
    try {
      // This would ideally be a dedicated API endpoint
      // For now, we'll use sample counts
      const counts = {};
      CROP_CATEGORIES.forEach(crop => {
        counts[crop.slug] = Math.floor(Math.random() * 20) + 3;
      });
      setCropCounts(counts);
    } catch (error) {
      console.error('Failed to fetch crop counts:', error);
    }
  };

  const fetchProducts = async (pageNum = 1, reset = false) => {
    if (pageNum === 1) setLoading(true);
    
    try {
      const params = new URLSearchParams();
      params.append('limit', pageSize);
      params.append('skip', (pageNum - 1) * pageSize);
      params.append('category', 'agriculture');
      
      if (searchQuery) {
        params.append('search', searchQuery);
      }
      
      if (selectedState && selectedState !== 'all') {
        params.append('state', selectedState);
      }

      if (selectedCrop) {
        params.append('crop_slug', selectedCrop);
      }
      
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/ads?${params.toString()}`
      );
      
      if (response.ok) {
        const data = await response.json();
        let newProducts = data.ads || [];
        
        // If no real products and we have a selected crop, show sample ads
        if (newProducts.length === 0 && selectedCrop && SAMPLE_ADS[selectedCrop]) {
          newProducts = SAMPLE_ADS[selectedCrop].map((sample, idx) => ({
            ad_id: `sample_${selectedCrop}_${idx}`,
            title: isArabic ? sample.title_ar : sample.title_en,
            price: sample.price,
            currency: 'SDG',
            state: sample.state,
            city: sample.city,
            location: sample.city,
            images: [],
            isSample: true,
            crop_slug: selectedCrop,
          }));
        }
        
        if (reset) {
          setProducts(newProducts);
        } else {
          setProducts(prev => [...prev, ...newProducts]);
        }
        
        setHasMore(newProducts.length === pageSize && !newProducts[0]?.isSample);
        setPage(pageNum);
      }
    } catch (error) {
      console.error('Failed to fetch products:', error);
      // Show sample ads on error
      if (selectedCrop && SAMPLE_ADS[selectedCrop]) {
        const sampleProducts = SAMPLE_ADS[selectedCrop].map((sample, idx) => ({
          ad_id: `sample_${selectedCrop}_${idx}`,
          title: isArabic ? sample.title_ar : sample.title_en,
          price: sample.price,
          currency: 'SDG',
          state: sample.state,
          city: sample.city,
          location: sample.city,
          images: [],
          isSample: true,
          crop_slug: selectedCrop,
        }));
        setProducts(sampleProducts);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCropClick = (cropSlug) => {
    if (selectedCrop === cropSlug) {
      // Toggle off if same crop clicked
      setSelectedCrop(null);
      navigate('/crops');
    } else {
      setSelectedCrop(cropSlug);
      navigate(`/crop/${cropSlug}`);
    }
  };

  const handlePostAd = () => {
    if (!user) {
      setLoginRedirectAction({ type: 'post' });
      setShowLoginModal(true);
      return;
    }
    const cropParam = selectedCrop ? `&crop=${selectedCrop}` : '';
    navigate(`/add-ad?category=agriculture${cropParam}`);
  };

  const handleLoginSuccess = () => {
    setShowLoginModal(false);
    if (loginRedirectAction) {
      if (loginRedirectAction.type === 'post') {
        handlePostAd();
      } else if (loginRedirectAction.type === 'contact') {
        navigate(`/ad/${loginRedirectAction.adId}`);
      }
    }
  };

  const handleLoadMore = () => {
    if (!loading && hasMore) {
      fetchProducts(page + 1, false);
    }
  };

  const handleLocationSelect = (stateKey) => {
    setSelectedState(stateKey);
    setShowLocationSheet(false);
  };

  const handleAdClick = (ad) => {
    if (ad.isSample) {
      // For sample ads, prompt to post
      handlePostAd();
    } else {
      navigate(`/ad/${ad.ad_id}`);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      fetchProducts(1, true);
    }
  };

  // ═══════════════════════════════════════════════════════════
  // UNIFIED SEARCH PIPELINE — single source of truth
  // normalize → filter → dedupe (shared with Home, Shop, etc.)
  // ═══════════════════════════════════════════════════════════
  const displayProducts = useUnifiedSearch(products, searchQuery);

  const clearCropFilter = () => {
    setSelectedCrop(null);
    navigate('/crops');
  };

  return (
    <div className="crop-home" data-testid="crop-home">
      {/* MARKET TICKER */}
      <MarketTicker 
        state={selectedState} 
        onTickerClick={() => navigate('/market-prices')}
      />
      
      {/* LOCATION SELECTOR - Modern clickable button */}
      <div className="location-bar">
        <button 
          className="location-selector"
          onClick={() => setShowLocationSheet(true)}
          data-testid="location-selector"
        >
          <MapPin size={18} className="location-icon" />
          <span className="location-text">
            {isArabic ? selectedStateData.name_ar : selectedStateData.name_en}
          </span>
          <ChevronRight size={18} className="chevron-icon" />
        </button>
      </div>

      {/* SEARCH BAR */}
      <div className="crop-search-bar">
        <div className="search-input-wrapper">
          <Search className="search-icon" size={18} />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder={t('crophome.searchCrops')}
            className="search-input"
            data-testid="crop-search-input"
          />
          {searchQuery && (
            <button className="clear-search" onClick={() => setSearchQuery('')}>
              <X size={16} />
            </button>
          )}
        </div>
      </div>
      
      {/* CROP FILTER CHIP (if a crop is selected) */}
      {selectedCrop && selectedCropData && (
        <div className="active-filters">
          <div className="filter-chip active">
            <span className="chip-icon">{selectedCropData.icon}</span>
            <span className="chip-text">
              {isArabic ? selectedCropData.name_ar : selectedCropData.name_en}
            </span>
            <button className="chip-clear" onClick={clearCropFilter}>
              <X size={14} />
            </button>
          </div>
        </div>
      )}
      
      {/* CROP CATEGORIES - FUNCTIONAL GRID */}
      {!selectedCrop && (
        <div className="crop-categories-section">
          <h2 className="section-title">
            {t('crophome.browseCrops')}
          </h2>
          <div className="crop-grid" data-testid="crop-grid">
            {CROP_CATEGORIES.map((crop) => (
              <button
                key={crop.slug}
                className={`crop-card ${selectedCrop === crop.slug ? 'selected' : ''}`}
                onClick={() => handleCropClick(crop.slug)}
                style={{ '--crop-color': crop.color }}
                data-testid={`crop-${crop.slug}`}
              >
                <div className="crop-card-icon">
                  <span className="crop-emoji">{crop.icon}</span>
                </div>
                <div className="crop-card-info">
                  <span className="crop-name">
                    {isRTL ? crop.name_ar : crop.name_en}
                  </span>
                  {crop.priceHint && (
                    <span className="crop-price-hint">
                      <TrendingUp size={12} />
                      {crop.priceHint} SDG
                    </span>
                  )}
                  {cropCounts[crop.slug] > 0 && (
                    <span className="crop-count">
                      {cropCounts[crop.slug]} {t('unitAds')}
                    </span>
                  )}
                </div>
                <ChevronRight size={18} className="crop-arrow" />
              </button>
            ))}
          </div>
        </div>
      )}
      
      {/* POST AD CTA */}
      <div className="post-ad-cta">
        <button 
          className="post-ad-btn"
          onClick={handlePostAd}
          data-testid="post-crop-btn"
        >
          <Plus size={20} />
          <span className="btn-text">
            {t('crophome.postYourCrop')}
          </span>
        </button>
      </div>
      
      {/* LISTINGS FEED */}
      <div className="listings-section">
        <div className="section-header">
          <h2 className="section-title">
            {selectedCropData 
              ? (isRTL ? `عروض ${selectedCropData.name_ar}` : `${selectedCropData.name_en} Listings`)
              : (t('crophome.latestListings'))}
          </h2>
          <button 
            className="refresh-btn"
            onClick={() => fetchProducts(1, true)}
            disabled={loading}
          >
            <RefreshCw size={16} className={loading ? 'spinning' : ''} />
          </button>
        </div>
        
        {loading && page === 1 ? (
          <div className="loading-state">
            <Loader2 size={32} className="spinning" />
            <p>{t('crophome.loading')}</p>
          </div>
        ) : displayProducts.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">
              <Package size={48} />
            </div>
            <h3>{t('crophome.noCropsFound')}</h3>
            <p>
              {t('crophome.beTheFirstToPost')}
            </p>
            <button className="btn-post-empty" onClick={handlePostAd}>
              <Plus size={18} />
              {t('crophome.postYourFirstAd')}
            </button>
          </div>
        ) : (
          <>
            {/* Sample ads notice */}
            {displayProducts[0]?.isSample && (
              <div className="sample-notice">
                <span>
                  {t('crophome.theseAreSampleAdsPost')}
                </span>
              </div>
            )}
            
            <div className="products-grid">
              {displayProducts.map((product) => (
                <div
                  key={product.ad_id}
                  className={`product-card ${product.isSample ? 'sample' : ''}`}
                  onClick={() => handleAdClick(product)}
                  data-testid={`product-${product.ad_id}`}
                >
                  <div className="product-image">
                    {product.images?.[0] ? (
                      <img 
                        src={getImageUrl(product.images[0])} 
                        alt={product.title}
                        loading="lazy"
                        onError={handleImageError}
                        onLoad={handleImageLoad}
                      />
                    ) : (
                      <div className="placeholder-image">
                        {selectedCropData?.icon || '🌾'}
                      </div>
                    )}
                    {!product.isSample && (
                      <button
                        onClick={(e) => toggleFavorite(product.ad_id, e)}
                        className={`favorite-btn ${isFavorite(product.ad_id) ? 'active' : ''}`}
                        data-testid={`favorite-${product.ad_id}`}
                      >
                        <Heart size={14} fill={isFavorite(product.ad_id) ? 'currentColor' : 'none'} />
                      </button>
                    )}
                    {product.isSample && (
                      <div className="sample-badge">
                        {t('crophome.sample')}
                      </div>
                    )}
                  </div>
                  <div className="product-info">
                    <h3 className="product-title">{product.title}</h3>
                    <p className="product-price">
                      {formatMoney(product.price, { locale: isArabic ? 'ar-SD' : 'en', currency: product.currency || 'SDG' })}
                    </p>
                    {(product.location || product.city) && (
                      <div className="product-location">
                        <MapPin size={12} />
                        <span>{product.city || product.location}</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
            
            {hasMore && !displayProducts[0]?.isSample && (
              <div className="load-more">
                <button 
                  className="btn-load-more"
                  onClick={handleLoadMore}
                  disabled={loading}
                >
                  {loading ? 
                    (t('crophome.loading')) : 
                    (t('crophome.loadMore'))}
                </button>
              </div>
            )}
          </>
        )}
      </div>
      
      {/* LOCATION BOTTOM SHEET */}
      <LocationSheet
        open={showLocationSheet}
        onClose={() => setShowLocationSheet(false)}
        onSelect={handleLocationSelect}
        selectedState={selectedState}
        states={SUDAN_STATES}
        isArabic={isArabic}
      />
      
      {/* LOGIN PROMPT MODAL */}
      <LoginPromptModal
        open={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        onLoginSuccess={handleLoginSuccess}
        message={t('crophome.signInToContinue')}
      />
    </div>
  );
}

export default CropHome;
